import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Sparkles, Wrench } from "lucide-react";

// Estrutura inicial de "Novidades da versão" (Pacote 2, item 11; separada em
// abas no Pacote de Correções, item 7). Sem CMS — lista estática mantida no
// código, como pedido explicitamente ("não precisa criar neste momento um
// CMS complexo"). Evolutiva: cada entrada já tem o formato (versão, data,
// novidades, ajustes_e_melhorias) que um futuro cadastro administrável
// poderia preencher dinamicamente sem mudar esta tela.
//
// "novidades" = funcionalidades NOVAS (ex.: Personalização, novos módulos,
// novos recursos). "ajustes_e_melhorias" = correções de bug, usabilidade,
// segurança, desempenho e refinamentos de funcionalidades já existentes —
// as duas categorias que antes ficavam misturadas numa lista só.
//
// Revisão final pré-produção (delta 2026-09-16, versão 3.0) — cada lista
// (`novidades`/`ajustes_e_melhorias`) aceita agora tanto o formato antigo
// (array de strings, releases 1.1/2.0/2.1 abaixo, sem nenhuma mudança)
// quanto um formato agrupado por assunto (array de
// `{ heading, items: string[] }`), usado só quando a release tem itens
// que fazem mais sentido separados (ex.: "Melhorias" vs "Correções" dentro
// da mesma aba "Ajustes e Melhorias", ou destacar "Admin Master" dentro de
// "Novidades") — ver `ReleaseCard` abaixo, que renderiza os dois formatos
// sem duplicar lógica. `data: null` é proposital: esta tarefa não faz
// deploy, então a versão 3.0 ainda não tem data de publicação real — a
// tela mostra "Data de publicação a definir" em vez de inventar uma data.
const RELEASES = [
  {
    versao: "3.0",
    data: "2026-09-17",
    novidades: [
      {
        heading: "Novos recursos",
        items: [
          "Botão \"A concluir\" na Ficha de Serviço — salva o que já foi preenchido sem enviar para aprovação e assinatura; a ficha pode ser retomada e concluída depois",
          "Guia de configuração do Asaas disponível direto no cadastro da conta, com a URL do Webhook pronta para copiar",
        ],
      },
      {
        heading: "Admin Master",
        items: [
          "Cada empresa passa a ter um número de contrato único e permanente, exibido nas cobranças",
          "Régua de inadimplência: notificações automáticas por e-mail em atrasos de cobrança (1, 5 e 9 dias); a partir de 10 dias de atraso, é possível antecipar manualmente a suspensão por inadimplência",
          "Monitor Fiscal: nova área para verificar, sob demanda, se houve atualização nas regras oficiais da Nota Fiscal de Serviço eletrônica — serve apenas para alertar, nunca altera o sistema sozinho",
          "Reforços de segurança na recuperação de acesso do Admin Master",
        ],
      },
    ],
    ajustes_e_melhorias: [
      {
        heading: "Melhorias",
        items: [
          "Nova organização da lista de Clientes, com cards de resumo (Total/Ativos/Inativos/Valor-hora médio), busca por nome, razão social, CPF/CNPJ, código ou cidade, e filtros por situação e cidade/UF",
          "Novo cadastro de Cliente em seções, com CPF/CNPJ como primeiro campo e confirmação antes de fechar sem salvar",
          "Cards de acompanhamento em Fichas de Serviço e em Faturamento & Cobrança, com contagem por status e filtro de um clique",
          "Diálogo de edição da Ficha de Serviço em tela cheia, com confirmação antes de fechar sem salvar",
          "Escolha simplificada da forma de recebimento do PIX quando a empresa tem mais de uma opção configurada (chave própria ou Asaas)",
          "\"Contas de Pagamento\" passou a se chamar \"Contas para Cobrança\", em Configurações → Financeiro",
          "Relatórios reorganizados em cartões por tipo, com resumo do período e exportação em PDF",
          "Página pública de assinatura de ficha com novo layout e opções de salvar em PDF e imprimir",
          "Menu lateral reorganizado: Usuários e permissões, Tipos de serviço, Grupos e Produtos/Sistemas agora têm páginas próprias dentro de Cadastros",
          "A fatura de assinatura da plataforma agora mostra o número do contrato da empresa",
        ],
      },
      {
        heading: "Correções",
        items: [
          "Em algumas telas, clicar em um card de status podia alterar temporariamente a contagem mostrada nos outros cards",
          "A tela de Configurações nem sempre voltava para a aba \"Geral\" ao ser reaberta",
          "O card \"Faturados\", em Faturamento & Cobrança, podia mostrar um número diferente da lista de boletos que ele mesmo abre",
          "A mensagem \"Nenhum boleto\" nem sempre aparecia quando um filtro não encontrava nenhum resultado",
          "A busca por número de contrato no painel comercial nem sempre reconhecia o número exatamente como exibido na tela",
          "O card de plano podia deixar de exibir corretamente os recursos premium já contratados",
          "Um problema que podia impedir indevidamente o acesso de um usuário logo após digitar a senha errada algumas vezes",
        ],
      },
    ],
  },
  {
    versao: "2.1",
    data: "2026-08-25",
    novidades: [
      "Tema Claro/Escuro — controle no cabeçalho, preferência salva no navegador",
      "Slogan/Subtítulo opcional em Configurações → Personalização, exibido no menu lateral",
    ],
    ajustes_e_melhorias: [
      "Faturamento: corrigido erro ao gerar faturamento com o filtro em \"Todos os clientes\" — o cliente agora é identificado automaticamente pelas fichas selecionadas",
      "Personalização: as cores agora são efetivamente aplicadas ao ambiente (menu, item ativo, botões principais, destaques, cabeçalho e fundo), inclusive na pré-visualização e em \"Restaurar padrão\"",
      "Grupos autorizados do Supervisor passaram a fazer parte da tela de Permissões, em vez de um botão separado",
    ],
  },
  {
    versao: "2.0",
    data: "2026-08-24",
    novidades: [
      "Identidade visual personalizável por empresa (logo, nome fantasia e cores) em Configurações → Personalização",
      "Cadastro de Grupos e vínculo de Produtos/Sistemas a Grupos",
      "Supervisor por Grupo — autorização de supervisores por Grupo de Produto/Sistema",
      "Tipo de contato estruturado na Ficha de Serviço",
      "Filtro por cliente no Dashboard",
      "Central de Ajuda e página Sobre",
    ],
    ajustes_e_melhorias: [
      "Fichas de Serviço: seleção de Produto/Sistema organizada por Grupo",
      "Auditoria: modal de detalhes sem códigos técnicos, mais legível",
    ],
  },
  {
    versao: "1.1",
    data: "2026-08-24",
    novidades: [
      "Cadastro de Produtos/Sistemas",
      "Faturamento por \"Todos os clientes\"",
      "Seletor de competência no Dashboard",
      "Upload de logo da empresa",
      "Auditoria com filtros, tradução de operações e visualização em detalhe",
    ],
    ajustes_e_melhorias: [
      "Nova Ficha de Serviço inicia com horários em branco (antes vinha pré-preenchida)",
    ],
  },
];

function isGroupedItems(items) {
  return items.length > 0 && typeof items[0] === "object" && items[0] !== null && "heading" in items[0];
}

function ReleaseCard({ r, items }) {
  const grouped = isGroupedItems(items);
  return (
    <Card className="p-5" data-testid={`release-${r.versao}`}>
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <Badge className="bg-primary/10 text-primary text-sm">Versão {r.versao}</Badge>
        <span className="text-xs text-slate-500" data-testid={`release-${r.versao}-data`}>
          {r.data ? new Date(r.data).toLocaleDateString("pt-BR") : "Data de publicação a definir"}
        </span>
      </div>
      {grouped ? (
        <div className="space-y-3">
          {items.map((grp, gi) => (
            <div key={gi}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{grp.heading}</div>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                {grp.items.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
          {items.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      )}
    </Card>
  );
}

export default function Novidades() {
  const comNovidades = RELEASES.filter(r => r.novidades.length > 0);
  const comAjustes = RELEASES.filter(r => r.ajustes_e_melhorias.length > 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><Sparkles size={28} className="text-primary" /> Novidades da versão</h1>
        <p className="text-slate-500 text-sm">Novos recursos, ajustes e melhorias do LOOTUS, organizados por versão</p>
      </div>

      <Tabs defaultValue="novidades">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="novidades" data-testid="tab-novidades-lista">Novidades</TabsTrigger>
          <TabsTrigger value="ajustes" data-testid="tab-ajustes-melhorias">Ajustes e Melhorias</TabsTrigger>
        </TabsList>

        <TabsContent value="novidades" className="mt-4">
          <div className="space-y-4">
            {comNovidades.map(r => <ReleaseCard key={r.versao} r={r} items={r.novidades} />)}
          </div>
        </TabsContent>

        <TabsContent value="ajustes" className="mt-4">
          <div className="space-y-4">
            {comAjustes.map(r => <ReleaseCard key={r.versao} r={r} items={r.ajustes_e_melhorias} />)}
            {comAjustes.length === 0 && (
              <Card className="p-8 text-center text-slate-400 flex flex-col items-center gap-2">
                <Wrench size={20} />
                Nenhum ajuste registrado ainda.
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
